import { identity, isString, sortBy, uniqueId } from 'lodash-es';
import React, { useMemo } from 'react';
import { mountRootParcel } from 'single-spa';

import { useUser } from 'hooks';
import icons from 'icons';
import { IconButton, styled } from 'netline-ui';
import services from 'services';
import {
  HeaderComponent,
  HeaderComponentItem,
  HeaderDividerItem,
  HeaderIconItem,
  HeaderItem,
  HeaderItemType,
  HeaderWidgetItem,
} from 'types';
import { logger } from 'utils';
import Divider from 'view/components/Divider/Divider';

import Parcel from '../../ApplicationModule/Parcel';
import HelpMenu from '../HelpMenu/HelpMenu';
import PreferencesMenu from '../PreferencesMenu/PreferencesMenu';
import UserInfo from '../UserInfo/UserInfo';

import styles, { appHeaderRightClasses } from './AppHeaderRight.styles';
import useHeaderItems from './useAppHeaderItems';

const isComponentItem = (item: HeaderItem): item is HeaderComponentItem => item.type === HeaderItemType.COMPONENT;
const isIconItem = (item: HeaderItem): item is HeaderIconItem => item.type === HeaderItemType.ICON;
const isWidgetItem = (item): item is HeaderWidgetItem => item.type === HeaderItemType.WIDGET;
const isDividerItem = (item): item is HeaderDividerItem => item.type === HeaderItemType.DIVIDER;

const AppHeaderRightRoot = styled('div', { name: appHeaderRightClasses.$name })(styles);

const headerComponentMap: Record<HeaderComponent, React.FC> = {
  [HeaderComponent.PreferencesMenu]: PreferencesMenu,
  [HeaderComponent.UserInfo]: UserInfo,
  [HeaderComponent.HelpMenu]: HelpMenu,
};

const isHeaderComponent = (component: React.FC | HeaderComponent): component is HeaderComponent => isString(component);

const getHeaderComponent = (component: React.FC | HeaderComponent) => {
  return isHeaderComponent(component) ? headerComponentMap[component] : component;
};

const sessionExpiredHeaderComponents = {
  [HeaderComponent.PreferencesMenu]: true,
};

const AppHeaderRight = () => {
  const headerItems = useHeaderItems();
  const { sessionIsExpired } = useUser();
  const visibleHeaderItems = useMemo(
    () =>
      sortBy(
        headerItems
          .filter((item) => (isComponentItem(item) ? !item.hidden : true))
          .filter(
            (item) =>
              !sessionIsExpired ||
              !isComponentItem(item) ||
              (isString(item.Component) && sessionExpiredHeaderComponents[item.Component]),
          ),
        'order',
      ),
    [headerItems, sessionIsExpired],
  );

  return (
    <AppHeaderRightRoot data-testid="AppHeaderRight-root" className={appHeaderRightClasses.root}>
      {visibleHeaderItems
        .map((item, idx) => {
          const nextItem = visibleHeaderItems[idx + 1];
          if (isDividerItem(item)) {
            return (
              nextItem &&
              !isDividerItem(nextItem) && (
                <Divider
                  key={uniqueId('Divider_')}
                  // @ts-ignore
                  orientation="vertical"
                  variant="middle"
                  data-appheaderitem
                  data-testid="AppHeaderRight-divider"
                />
              )
            );
          }

          const widgetConfig = isWidgetItem(item) && services.getWidget(item.widget);
          if (widgetConfig) {
            return (
              <Parcel
                key={item.widget}
                wrapClassName="AppHeaderRight-parcel"
                config={widgetConfig}
                mountParcel={mountRootParcel}
                services={services}
              />
            );
          }

          if (isIconItem(item)) {
            const Icon = icons[item.icon];
            const { id, type, icon, ...iconButtonProps } = item;
            return (
              <IconButton key={id} data-appheaderitem {...iconButtonProps} className={appHeaderRightClasses.iconButton}>
                <Icon />
              </IconButton>
            );
          }

          if (isComponentItem(item)) {
            const { Component: component, hidden, id, order, type, ...componentProps } = item;
            const Component = getHeaderComponent(component);
            return <Component key={id} data-appheaderitem {...componentProps} />;
          }

          logger.error('Item is not an appFrame header item!', item);
          return null;
        })
        .filter(identity)}
    </AppHeaderRightRoot>
  );
};

export default AppHeaderRight;
